'use client';

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

/**
 * Single-flag arbiter for the one sticky element allowed at the top of a TI
 * provider page (CMS-1643 AC-13). The M5 nav's `GlobalStickyPanel` claims the
 * slot when it pins; M2 (`TIOptions`) suspends its own pinning while the slot is
 * claimed, so the two never stack. Not a generic slot system — just one boolean.
 *
 * The provider is mounted page-wide (PageContent); on pages without the TI nav
 * nothing ever claims the slot, so `navPinned` stays false and M2 behaves as
 * before.
 */
type StickyTopSlotContextValue = {
  navPinned: boolean;
  setNavPinned: (pinned: boolean) => void;
};

const StickyTopSlotContext = createContext<StickyTopSlotContextValue>({
  navPinned: false,
  setNavPinned: () => {},
});

export const StickyTopSlotProvider = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const [navPinned, setNavPinned] = useState(false);
  const value = useMemo(() => ({ navPinned, setNavPinned }), [navPinned]);
  return (
    <StickyTopSlotContext.Provider value={value}>
      {children}
    </StickyTopSlotContext.Provider>
  );
};

/**
 * Read whether the top slot is claimed by the nav — M2 passes this to its
 * `GlobalStickyPanel` `suspended` prop.
 */
export const useStickyTopSlotSuspended = (): boolean =>
  useContext(StickyTopSlotContext).navPinned;

/**
 * Returns a reporter the nav feeds its `GlobalStickyPanel` `onPinnedChange`
 * callback. Releases the claim (sets false) on unmount so a nav that disappears
 * never leaves M2 permanently suspended.
 */
export const useClaimStickyTopSlot = (): ((pinned: boolean) => void) => {
  const { setNavPinned } = useContext(StickyTopSlotContext);
  useEffect(() => () => setNavPinned(false), [setNavPinned]);
  return useCallback((pinned: boolean) => setNavPinned(pinned), [setNavPinned]);
};
